import { Stack, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers'
// 以下のESLintエラーは回避策がないようなので無視する https://github.com/import-js/eslint-plugin-import/issues/1479
// eslint-disable-next-line import/no-duplicates
import { getHours, getMinutes, subMinutes } from 'date-fns'
// eslint-disable-next-line import/no-duplicates
import { ja } from 'date-fns/locale'
import type React from 'react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import type { TBaseFormRowProps } from './EditSchedule'

const SchedulePeriodPerDayRow: React.FC<TBaseFormRowProps> = ({
  setFieldValue,
  values: { startTime, endTime },
  errors,
  submitCount,
}) => {
  const start = startTime !== undefined ? startTime : null
  const end = endTime !== undefined ? endTime : null

  const onChange = (type: 'start' | 'end') => (date: Date | null) => {
    if (type === 'end') {
      setFieldValue(
        'endTime',
        getEndTime(start ?? undefined, date ?? undefined),
      )
    } else {
      setFieldValue('startTime', date)
      // 開始時刻を変更した場合でも、開始時刻と終了時刻が一致しているかを確認し、
      // 一致する場合は終了時刻を 1 分引くように変更する必要がある
      setFieldValue('endTime', getEndTime(date ?? undefined, end ?? undefined))
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          時刻
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TimePicker
            format="HH:mm"
            value={start}
            onChange={onChange('start')}
            // デジタル表示のみにする
            viewRenderers={{
              hours: renderMultiSectionDigitalClockTimeView,
              minutes: renderMultiSectionDigitalClockTimeView,
            }}
            slotProps={{
              textField: {
                slotProps: {
                  htmlInput: {
                    placeholder: '開始時刻',
                  },
                },
              },
              toolbar: {
                hidden: true,
              },
              layout: {
                sx: {
                  '.MuiPickersLayout-contentWrapper': {
                    gridColumn: '1 / 4',
                  },
                  '& .MuiList-root': {
                    flex: 1,
                  },
                  '& .MuiMenuItem-root': {
                    width: '100%',
                    margin: 0,
                  },
                },
              },
            }}
            sx={{ flex: 1 }}
          />
          <Typography>〜</Typography>
          <TimePicker
            format="HH:mm"
            value={end}
            onChange={onChange('end')}
            viewRenderers={{
              hours: renderMultiSectionDigitalClockTimeView,
              minutes: renderMultiSectionDigitalClockTimeView,
            }}
            slotProps={{
              textField: {
                slotProps: {
                  htmlInput: {
                    placeholder: '終了時刻',
                  },
                },
              },
              toolbar: {
                hidden: true,
              },
              layout: {
                sx: {
                  '.MuiPickersLayout-contentWrapper': {
                    gridColumn: '1 / 4',
                  },
                  '& .MuiList-root': {
                    flex: 1,
                  },
                  '& .MuiMenuItem-root': {
                    width: '100%',
                    margin: 0,
                  },
                },
              },
            }}
            sx={{ flex: 1 }}
          />
        </Stack>
        {submitCount > 0 && (
          <FormikErrorMessage name="startTime" errors={errors} />
        )}
        {submitCount > 0 && (
          <FormikErrorMessage name="endTime" errors={errors} />
        )}
      </Stack>
    </LocalizationProvider>
  )
}

const getEndTime = (startTime?: Date, endTime?: Date) => {
  // 開始時刻と終了時刻が同じ場合は 24 時間を設定しようとしていると見なして終了時刻から 1 分引く
  // e.g. 21:00 〜 21:00 -> 21:00 〜 20:59
  if (
    startTime &&
    endTime &&
    getHours(startTime) === getHours(endTime) &&
    getMinutes(startTime) === getMinutes(endTime)
  ) {
    return subMinutes(endTime, 1)
  } else {
    return endTime
  }
}

export default SchedulePeriodPerDayRow
